import React, { createElement, FunctionComponent } from 'react';
import styled from '@emotion/styled';
import { createRoot, Root } from 'react-dom/client';
import flatten from 'lodash-es/flatten';
import { getContainerStyle } from './styles';
import type { NotificationComponentProps } from '../notification/Notification';
import { NotificationComponent } from '../notification/Notification';
import type { ToastComponentProps } from '../toast/Toast';
import { ToastComponent } from '../toast/Toast';
import type { MessageDirection, MessagePositionType } from './types';
import { getZIndex } from '../../styles';

export interface MessageContainerProps {
  /**
   * @desc 메세지 노출 위치
   * @default 'top-center'
   */
  position: MessagePositionType;
  /**
   * @desc position 기준으로 커스텀 위치, center일 경우 적용되지 않습니다
   */
  offset_x: number;
  /**
   * @desc position 기준으로 커스텀 위치, center일 경우 적용되지 않습니다.
   */
  offset_y: number;
  /**
   * @desc 최대 노출 메세지 갯수
   * @default 6
   */
  max_count?: number;
  /**
   * @default 'toast'일 경우 1050, 'notification'일 경우 1040
   */
  z_index?: number;
  /** @default 'column' */
  direction?: MessageDirection;
  /** @default false */
  use_inline?: boolean;
}

type MessageContainerType = 'toast' | 'notification';
type MessageComponentTypes = ToastComponentProps | NotificationComponentProps;
export type MessageContainerKey = `${MessagePositionType}_${MessageDirection}_${number}_${number}`;

export type MessageOptions<T extends MessageComponentTypes> = Omit<T, 'destroy' | 'position'> &
  Partial<MessageContainerProps>;

export class MessageManager<T extends MessageComponentTypes> {
  private root: Root;
  private component: FunctionComponent<T>;
  private type: MessageContainerType;
  private z_index: number;
  private default_max_count: number;
  private default_offset: number;
  private default_position: MessagePositionType;
  private default_direction: MessageDirection;
  private container_keys: MessageContainerKey[] = [];
  private messages: Record<MessageContainerKey, T[] | []> | {} = {};
  private use_inline = false;
  private static instance: {
    toast: MessageManager<ToastComponentProps>;
    notification: MessageManager<NotificationComponentProps>;
  };

  constructor({
    component,
    type,
    z_index,
    default_max_count = 6,
    default_offset = 40,
    default_position = 'top-center',
    default_direction = 'column',
    root_container,
  }: {
    component: FunctionComponent<T>;
    type: MessageContainerType;
    z_index?: number;
    default_max_count?: number;
    default_offset?: number;
    default_position?: MessagePositionType;
    default_direction?: MessageDirection;
    root_container?: HTMLElement;
  }) {
    if (root_container) {
      this.root = createRoot(root_container);
      this.use_inline = true;
    } else {
      const container = document.createElement('div');
      container.id = `pds-${type}-container`;
      document.body.appendChild(container);
      this.root = createRoot(container);
    }

    this.component = component;
    this.z_index = z_index ?? getZIndex(type);
    this.type = type;
    this.default_max_count = default_max_count;
    this.default_offset = default_offset;
    this.default_position = default_position;
    this.default_direction = default_direction;
  }

  static getInstance<T extends MessageContainerType>(
    type: T,
  ): T extends 'toast' ? MessageManager<ToastComponentProps> : MessageManager<NotificationComponentProps> {
    if (!MessageManager.instance) {
      const instance = {
        toast: new MessageManager<ToastComponentProps>({
          type: 'toast',
          component: ToastComponent,
        }),
        notification: new MessageManager<NotificationComponentProps>({
          type: 'notification',
          component: NotificationComponent,
        }),
      };
      MessageManager.instance = instance;
    }
    if (type === 'toast') {
      return MessageManager.instance['toast'] as T extends 'toast' ? MessageManager<ToastComponentProps> : never;
    } else {
      return MessageManager.instance['notification'] as T extends 'toast'
        ? never
        : MessageManager<NotificationComponentProps>;
    }
  }

  set setInstance({
    default_max_count,
    default_offset,
    default_position,
  }: {
    default_max_count: number;
    default_offset: number;
    default_position: MessagePositionType;
  }) {
    this.default_max_count = default_max_count;
    this.default_offset = default_offset;
    this.default_position = default_position;
  }

  static generateContainerKey({ position, direction, offset_x, offset_y }: MessageContainerProps) {
    return `${position}_${direction}_${offset_x}_${offset_y}`;
  }

  static getContainerPropsByKey(key: MessageContainerKey) {
    const [position, direction, offset_x, offset_y] = key.split('_');
    return {
      position,
      direction,
      offset_x: Number(offset_x),
      offset_y: Number(offset_y),
    } as MessageContainerProps;
  }

  private generateMessageId() {
    return Math.random().toString(36).slice(2, 11);
  }

  private findContainerKeyById(id: string): MessageContainerKey | undefined {
    let container_key;
    // @ts-expect-error: TS2345
    Object.entries(this.messages).forEach(([key, messages]: [MessageContainerKey, T[]]) => {
      if (messages.find((message) => message.id === id)) {
        container_key = key;
      }
    });
    return container_key;
  }

  private addContainerKey(key: MessageContainerKey): void {
    this.container_keys = [...this.container_keys, key];
  }

  private removeContainerKey(key: MessageContainerKey): void {
    this.container_keys = this.container_keys.filter((container_key) => container_key !== key);
  }

  private getOffset(offset_x?: number, offset_y?: number) {
    return {
      offset_x: offset_x ?? this.default_offset,
      offset_y: offset_y ?? this.default_offset,
    };
  }

  private render() {
    const toast_containers = this.container_keys.map((key) => (
      <MessageContainer
        key={key}
        {...MessageManager.getContainerPropsByKey(key)}
        type={this.type}
        use_inline={this.use_inline}
        z_index={this.z_index}
      >
        {/* @ts-expect-error: TS7053*/}
        {this.messages[key]?.map((toastProps: T) => {
          return createElement(this.component, {
            key: toastProps.id,
            ...toastProps,
          });
        })}
      </MessageContainer>
    ));
    this.root.render(toast_containers);
  }

  public show({
    id,
    position = this.default_position,
    offset_x,
    offset_y,
    max_count = this.default_max_count,
    direction = this.default_direction,
    ...options
  }: MessageOptions<T>): void {
    const current_toast_list = flatten(Object.values(this.messages));
    if (current_toast_list.length > max_count - 1) return;
    const container_key = MessageManager.generateContainerKey({
      position,
      direction,
      ...this.getOffset(offset_x, offset_y),
    });
    const toast_id = id ?? this.generateMessageId();
    const toast: Partial<MessageComponentTypes> = {
      ...options,
      id: toast_id,
      position,
      direction,
      destroy: () => this.destroy(toast_id),
    };
    // @ts-expect-error: TS7053
    this.messages = { ...this.messages, ...{ [container_key]: [...(this.messages[container_key] ?? []), toast] } };
    // @ts-expect-error: TS2345
    if (!this.container_keys.includes(container_key)) {
      // @ts-expect-error: TS2345
      this.addContainerKey(container_key);
    }
    this.render();
  }

  public destroy(id: string, key?: MessageContainerKey): void {
    const container_key = key ?? this.findContainerKeyById(id);
    if (!container_key) {
      return;
    }
    // @ts-expect-error: TS7053
    this.messages[container_key] = this.messages[container_key].filter((toast: T) => toast.id !== id);
    // @ts-expect-error: TS2345
    if (this.messages[container_key].length === 0) {
      this.removeContainerKey(container_key);
    }
    this.render();
  }
}

interface MessageContainerComponentProps extends MessageContainerProps {
  type: MessageContainerType;
}

const MessageContainer = styled.div<MessageContainerComponentProps>`
  position: ${({ use_inline }) => (use_inline ? 'absolute' : 'fixed')};
  ${({ type, offset_x }) =>
    type === 'notification' &&
    `
      width: calc(100% - ${(offset_x || 0) * 2}px);
      height: 0;
    `};
  ${({ z_index }) => `z-index: ${z_index}`};
  display: flex;
  flex-direction: ${({ direction }) => direction};
  ${(props) => getContainerStyle(props)}
  row-gap: 8px
`;
